/**
 * The one dropdown (replaces every native <select> in the dashboard). A <button aria-haspopup="listbox"> opens a
 * listbox rendered in a portal on document.body, positioned with position: fixed under the button (above it when
 * there's no room below, kept inside the viewport), and kept attached while any container scrolls or the window
 * resizes; it closes if the button scrolls out of view. Focus stays on the button (aria-activedescendant).
 *
 * Keyboard: Enter / Space / ↑ / ↓ open; ↑ ↓ Home End move; typing jumps by first letters; Enter / Space pick;
 * Escape closes (and doesn't reach the dialog underneath); Tab closes. A press outside closes.
 *
 * Styling: with no `className` the button is the boxed control (`.dd-trigger--box`, like `.btn`). With a
 * `className` the button is unstyled apart from layout, so it takes the look of the class it's given
 * (`.role-select`, `.tset-pill`, `.cs-select`, …). `children` replace the button's content entirely.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";

export interface DropdownOption<T extends string | number> {
  value: T;
  label: string;
  hint?: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface DropdownProps<T extends string | number> {
  value: T;
  options: readonly DropdownOption<T>[];
  onChange: (value: T) => void;
  /** aria-label of the button. */
  label?: string;
  id?: string;
  /** Leading icon inside the button. */
  icon?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  className?: string;
  align?: "start" | "end";
  /** Text shown on the button instead of the selected option's label. */
  display?: ReactNode;
  /** The chevron at the end of the button; false for none. */
  chevron?: ReactNode | false;
  /** Replace the button's whole content. */
  children?: ReactNode;
  /** Submitted with a <form> under this name (hidden input). */
  name?: string;
  /** Extra data-* attributes for the button. */
  data?: Record<`data-${string}`, string | boolean | undefined>;
}

const MAX_H = 320, GAP = 4, MARGIN = 8;

export function Dropdown<T extends string | number>(p: DropdownProps<T>) {
  const { value, options, onChange, label, icon, placeholder, disabled, size = "md", className, align = "start", display, children, name, data } = p;
  const uid = useId();
  const id = p.id ?? `dd${uid.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const listId = `${id}-list`;
  const optId = (i: number) => `${id}-opt-${i}`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const trigger = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLUListElement>(null);
  const typed = useRef({ text: "", at: 0 });

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;
  const enabled = (i: number) => i >= 0 && i < options.length && !options[i].disabled;
  const step = (from: number, dir: 1 | -1) => {
    for (let i = from + dir; i >= 0 && i < options.length; i += dir) if (enabled(i)) return i;
    return enabled(from) ? from : -1;
  };
  const first = () => step(-1, 1);
  const last = () => step(options.length, -1);

  const close = useCallback(() => setOpen(false), []);
  const show = (at: number) => { if (disabled) return; setActive(at); setOpen(true); };
  const pick = (i: number) => {
    if (!enabled(i)) return;
    setOpen(false);
    trigger.current?.focus();
    if (options[i].value !== value) onChange(options[i].value);
  };

  /** Place the list under (or above) the button; close when the button has left the viewport. */
  const place = useCallback(() => {
    const t = trigger.current, el = pop.current;
    if (!t || !el) return;
    const r = t.getBoundingClientRect();
    const vw = document.documentElement.clientWidth || innerWidth, vh = innerHeight;
    if (!t.isConnected || r.bottom < 0 || r.top > vh || r.right < 0 || r.left > vw || (r.width === 0 && r.height === 0)) { setOpen(false); return; }
    el.style.minWidth = `${Math.min(r.width, vw - MARGIN * 2)}px`;
    el.style.maxWidth = `${vw - MARGIN * 2}px`;
    const natural = Math.min(el.scrollHeight, MAX_H);
    const below = vh - r.bottom - GAP - MARGIN, above = r.top - GAP - MARGIN;
    const up = below < natural && above > below;
    const h = Math.max(0, Math.min(natural, up ? above : below));
    el.style.maxHeight = `${Math.min(MAX_H, up ? above : below)}px`;
    el.style.top = `${up ? r.top - GAP - h : r.bottom + GAP}px`;
    el.dataset.placement = up ? "top" : "bottom";
    const w = el.offsetWidth;
    const want = align === "end" ? r.right - w : r.left;
    el.style.left = `${Math.max(MARGIN, Math.min(want, vw - MARGIN - w))}px`;
  }, [align]);

  useLayoutEffect(() => { if (open) place(); }, [open, place, options.length]);

  // Follow the button while anything scrolls (the page scrolls inside .panel, not the window) or the window resizes.
  useEffect(() => {
    if (!open) return;
    let frame = 0;
    const later = (e: Event) => {
      if (e.type === "scroll" && pop.current?.contains(e.target as Node)) return;   // scrolling the list itself
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };
    const down = (e: PointerEvent) => {
      const n = e.target as Node;
      if (!trigger.current?.contains(n) && !pop.current?.contains(n)) setOpen(false);
    };
    // Window capture runs before the dialogs' own Escape handlers (document capture, window bubble), so Escape only
    // closes the list.
    const esc = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault(); e.stopPropagation();
      setOpen(false);
      trigger.current?.focus();
    };
    addEventListener("scroll", later, true);
    addEventListener("resize", later);
    addEventListener("pointerdown", down, true);
    addEventListener("keydown", esc, true);
    return () => {
      cancelAnimationFrame(frame);
      removeEventListener("scroll", later, true);
      removeEventListener("resize", later);
      removeEventListener("pointerdown", down, true);
      removeEventListener("keydown", esc, true);
    };
  }, [open, place]);

  // Keep the active option in view inside the list.
  useEffect(() => {
    if (!open || active < 0) return;
    const el = pop.current, o = document.getElementById(optId(active));
    if (!el || !o) return;
    if (o.offsetTop < el.scrollTop) el.scrollTop = o.offsetTop - 4;
    else if (o.offsetTop + o.offsetHeight > el.scrollTop + el.clientHeight) el.scrollTop = o.offsetTop + o.offsetHeight - el.clientHeight + 4;
  }, [open, active]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Typeahead: letters typed within half a second build a prefix; repeating one letter cycles through matches. */
  const typeahead = (ch: string) => {
    const now = Date.now(), t = typed.current;
    t.text = now - t.at > 500 ? ch : t.text + ch;
    t.at = now;
    const q = t.text.toLowerCase();
    const cycling = q.length > 1 && [...q].every((c) => c === q[0]);
    const needle = cycling ? q[0] : q;
    const from = open ? active : selectedIndex;
    const start = cycling || q.length === 1 ? from + 1 : Math.max(from, 0);
    for (let k = 0; k < options.length; k++) {
      const i = (start + k) % options.length;
      if (enabled(i) && options[i].label.toLowerCase().trimStart().startsWith(needle)) return i;
    }
    return -1;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    const k = e.key;
    if (!open) {
      if (k === "ArrowDown" || k === "ArrowUp" || k === "Enter" || k === " ") {
        e.preventDefault();
        show(enabled(selectedIndex) ? selectedIndex : k === "ArrowUp" ? last() : first());
      } else if (k.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const i = typeahead(k);
        show(i >= 0 ? i : enabled(selectedIndex) ? selectedIndex : first());
      }
      return;
    }
    if (k === "ArrowDown") { e.preventDefault(); setActive(active < 0 ? first() : step(active, 1)); }
    else if (k === "ArrowUp") { e.preventDefault(); setActive(active < 0 ? last() : step(active, -1)); }
    else if (k === "Home" || k === "PageUp") { e.preventDefault(); setActive(first()); }
    else if (k === "End" || k === "PageDown") { e.preventDefault(); setActive(last()); }
    else if (k === "Enter" || (k === " " && !(typed.current.text && Date.now() - typed.current.at < 500))) { e.preventDefault(); pick(active); }
    else if (k === "Tab") setOpen(false);
    else if (k.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) { e.preventDefault(); const i = typeahead(k); if (i >= 0) setActive(i); }
  };

  const custom = className !== undefined;
  const cls = ["dd-trigger", custom ? className : `dd-trigger--box dd-trigger--${size}`].join(" ");
  const chevron = p.chevron === undefined ? <ChevronDown size={size === "sm" ? 13 : 14} className="dd-chev" aria-hidden /> : p.chevron;
  const shown = display ?? selected?.label ?? placeholder ?? "";

  return (
    <>
      <button ref={trigger} type="button" id={id} className={cls} {...data} disabled={disabled}
        aria-label={label} aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? optId(active) : undefined}
        onClick={() => (open ? setOpen(false) : show(enabled(selectedIndex) ? selectedIndex : first()))}
        onKeyDown={onKeyDown} onKeyUp={(e) => { if (e.key === " ") e.preventDefault(); }}>
        {children ?? <>{icon}<span className={`dd-value${selected || display ? "" : " is-placeholder"}`}>{shown}</span>{chevron || null}</>}
      </button>
      {name !== undefined && <input type="hidden" name={name} value={String(value)} />}
      {open && createPortal(
        <ul ref={pop} id={listId} role="listbox" aria-label={label} aria-labelledby={label ? undefined : id} className={`dd-pop dd-pop--${size}`}
          onMouseDown={(e) => e.preventDefault()}>
          {options.map((o, i) => (
            <li key={String(o.value)} id={optId(i)} role="option" aria-selected={o.value === value} aria-disabled={o.disabled || undefined}
              className={`dd-option${i === active ? " is-active" : ""}`}
              onPointerMove={() => { if (!o.disabled && i !== active) setActive(i); }}
              onClick={() => pick(i)}>
              {o.icon && <span className="dd-option__icon" aria-hidden>{o.icon}</span>}
              <span className="dd-option__text">{o.label || " "}{o.hint && <small>{o.hint}</small>}</span>
              <Check size={14} className="dd-option__check" aria-hidden />
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </>
  );
}
